import { useEffect, useState } from 'react';
import { MdAdd, MdRemoveRedEye, MdSearch, MdEdit, MdDelete, MdToggleOff, MdToggleOn, MdClose } from 'react-icons/md';
import {
  createUser,
  grantPremiumAccess,
  checkPremiumAccess,
  revokePremiumAccess,
  fetchStudentScholarships,
  revokeSpecificScholarship,
  revokeTermFromScholarship,
  subscribeToUsers,
  updateUser,
  toggleUserStatus,
  deleteUserAccount,
} from '../../services/userService';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import { CLASS_NAMES, USER_ROLES } from '../../utils/constants';

const ALL_SUBJECT_LIST = [
  { id: 'math', name: 'Mathematics' },
  { id: 'english', name: 'English Language' },
  { id: 'physics', name: 'Physics' },
  { id: 'chem', name: 'Chemistry' },
  { id: 'bio', name: 'Biology' },
  { id: 'fmaths', name: 'Further Mathematics' },
  { id: 'agric', name: 'Agricultural Science' },
  { id: 'economics', name: 'Economics' },
  { id: 'accounting', name: 'Accounting' },
  { id: 'fin_accounting', name: 'Financial Accounting' },
  { id: 'marketing', name: 'Marketing' },
  { id: 'literature', name: 'Literature in English' },
  { id: 'govt', name: 'Government' },
];

const JSS_SUBJECT_LIST = [
  { id: 'math', name: 'Mathematics' },
  { id: 'english', name: 'English Language' },
  { id: 'basic_science', name: 'Basic Science' },
  { id: 'basic_tech', name: 'Basic Technology' },
  { id: 'agric', name: 'Agricultural Science' },
  { id: 'social_studies', name: 'Social Studies' },
  { id: 'civic', name: 'Civic Education' },
  { id: 'business_studies', name: 'Business Studies' },
  { id: 'ict', name: 'ICT / Computer Studies' },
  { id: 'phe', name: 'Physical & Health Education' },
  { id: 'home_econ', name: 'Home Economics' },
  { id: 'cca', name: 'Cultural & Creative Arts' },
  { id: 'crs', name: 'Christian Religious Studies' },
  { id: 'irs', name: 'Islamic Religious Studies' },
];

const getAvailableSubjectsForStudent = (student) => {
  if (!student) return [];
  const classLevel = (student.classLevel || 'ss1').toLowerCase();
  
  if (classLevel.startsWith('jss')) {
    return JSS_SUBJECT_LIST.map((subj) => ({
      id: `${subj.id}_${classLevel}`,
      name: subj.name,
    }));
  }

  if (classLevel === 'utme') {
    return ALL_SUBJECT_LIST.map((subj) => ({
      id: `${subj.id}_utme`,
      name: `${subj.name} (UTME)`,
    }));
  }

  return ALL_SUBJECT_LIST.map((subj) => ({
    id: `${subj.id}_${classLevel}`,
    name: subj.name,
  }));
};

const isSeniorClass = (lvl) => {
  const c = (lvl || '').toLowerCase().trim();
  return c === 'ss1' || c === 'ss2' || c === 'ss3';
};

const formatReferral = (val) => {
  if (!val) return 'None';
  const str = String(val).trim();
  if (
    str === '' ||
    str === '—' ||
    str === 'â€”' ||
    str === '-' ||
    str === '--' ||
    str.toLowerCase() === 'none' ||
    str.toLowerCase() === 'null' ||
    str.toLowerCase() === 'undefined'
  ) {
    return 'None';
  }
  return str;
};

const CLEAN_SUBJECT_DISPLAY_NAMES = {
  math: 'Mathematics',
  mathematics: 'Mathematics',
  maths: 'Mathematics',
  english: 'English',
  english_studies: 'English',
  english_language: 'English',
  physics: 'Physics',
  phy: 'Physics',
  chem: 'Chemistry',
  chemistry: 'Chemistry',
  bio: 'Biology',
  biology: 'Biology',
  fmaths: 'Further Mathematics',
  further_mathematics: 'Further Mathematics',
  further_maths: 'Further Mathematics',
  agric: 'Agricultural Science',
  agricultural_science: 'Agricultural Science',
  economics: 'Economics',
  econ: 'Economics',
  accounting: 'Accounting',
  fin_accounting: 'Financial Accounting',
  marketing: 'Marketing',
  literature: 'Literature in English',
  lit: 'Literature in English',
  govt: 'Government',
  government: 'Government',
  basic_science: 'Basic Science',
  intermediate_science: 'Basic Science',
  basic_tech: 'Basic Technology',
  digital_technologies: 'Basic Technology',
  digital_tech: 'Basic Technology',
  ict: 'ICT / Computer Studies',
  social_studies: 'Social Studies',
  social_citizenship: 'Social Studies',
  civic: 'Civic Education',
  business_studies: 'Business Studies',
  phe: 'Physical & Health Education',
  home_econ: 'Home Economics',
  cca: 'Cultural & Creative Arts',
  crs: 'Christian Religious Studies',
  irs: 'Islamic Religious Studies',
};

const formatSubjectTitle = (subjectId, student) => {
  if (!subjectId) return 'Unknown Subject';
  if (subjectId === '_class_package') return 'Full Class Package';

  const clean = subjectId.toLowerCase().trim();
  const classMatch = clean.match(/_(ss1|ss2|ss3|jss1|jss2|jss3|utme)$/);
  const baseKey = classMatch ? clean.replace(classMatch[0], '') : clean;

  if (CLEAN_SUBJECT_DISPLAY_NAMES[baseKey]) {
    return CLEAN_SUBJECT_DISPLAY_NAMES[baseKey];
  }

  const available = getAvailableSubjectsForStudent(student);
  const found = available.find((s) => s.id === subjectId || s.id.startsWith(baseKey));
  if (found) return found.name;

  return baseKey.charAt(0).toUpperCase() + baseKey.slice(1);
};

const Students = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newStudent, setNewStudent] = useState({
    fullName: '',
    email: '',
    password: '',
    classLevel: '',
    stream: '',
    phoneNumber: '',
    isActive: true,
  });
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [classFilter, setClassFilter] = useState('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [showViewModal, setShowViewModal] = useState(false);
  const [studentToView, setStudentToView] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [grantingPremium, setGrantingPremium] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [checkingPremium, setCheckingPremium] = useState(false);
  const [scholarshipDetails, setScholarshipDetails] = useState({
    hasScholarship: false,
    classPackage: null,
    subjects: [],
    totalActive: 0,
  });
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [showScholarshipModal, setShowScholarshipModal] = useState(false);
  const [scholarshipDuration, setScholarshipDuration] = useState(12);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Advanced Scholarship Options State
  const [scholarshipAccessMode, setScholarshipAccessMode] = useState('monthly'); // 'monthly' | 'term'
  const [scholarshipTerm, setScholarshipTerm] = useState(1); // 1, 2, 3
  const [scholarshipTier, setScholarshipTier] = useState('class'); // 'class', 'single', 'bundle_3', 'bundle_5'
  const [scholarshipSelectedSubjects, setScholarshipSelectedSubjects] = useState([]);

  const handleSubjectToggle = (subjectId) => {
    const maxAllowed = scholarshipTier === 'single' ? 1 : (scholarshipTier === 'bundle_3' ? 3 : 5);
    
    if (scholarshipSelectedSubjects.includes(subjectId)) {
      setScholarshipSelectedSubjects(scholarshipSelectedSubjects.filter(id => id !== subjectId));
    } else {
      if (scholarshipSelectedSubjects.length >= maxAllowed) {
        if (maxAllowed === 1) {
          setScholarshipSelectedSubjects([subjectId]);
        } else {
          alert(`You can only select up to ${maxAllowed} subjects for this package.`);
        }
      } else {
        setScholarshipSelectedSubjects([...scholarshipSelectedSubjects, subjectId]);
      }
    }
  };

  // Subscribe to students data
  useEffect(() => {
    const unsubscribe = subscribeToUsers((usersData) => {
      const studentsOnly = usersData.filter(user => user.role === USER_ROLES.STUDENT);
      setStudents(studentsOnly);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Filter students by class, subscription, and search
  useEffect(() => {
    let filtered = students;

    if (classFilter !== 'all') {
      filtered = filtered.filter(student => (student.classLevel || '').toLowerCase() === classFilter.toLowerCase());
    }

    if (subscriptionFilter !== 'all') {
      filtered = filtered.filter(student => {
        const hasSubscription = student.subscriptions && student.subscriptions.length > 0;
        return subscriptionFilter === 'subscribed' ? hasSubscription : !hasSubscription;
      });
    }

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        student =>
          student.fullName?.toLowerCase().includes(lowerSearch) ||
          student.email?.toLowerCase().includes(lowerSearch)
      );
    }

    setFilteredStudents(filtered);
  }, [students, classFilter, subscriptionFilter, searchTerm]);

  const handleToggleStatus = async (student) => {
    try {
      await toggleUserStatus(student.id, student.isActive);
    } catch (error) {
      alert('Failed to update student status');
    }
  };

  const handleDeleteStudent = async () => {
    if (!studentToDelete) return;

    setIsDeleting(true);
    try {
      await deleteUserAccount(studentToDelete.id);
      setShowDeleteModal(false);
      setStudentToDelete(null);
      alert('Success! Student account deleted successfully.');
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to delete student';
      alert(`Error deleting student:\n\n${errorMessage}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const reloadScholarships = async (studentId) => {
    setCheckingPremium(true);
    try {
      const details = await fetchStudentScholarships(studentId);
      setScholarshipDetails(details);
      setIsPremium(details.hasScholarship);
    } catch (err) {
      console.error('Error fetching scholarships:', err);
    } finally {
      setCheckingPremium(false);
    }
  };

  const handleViewStudent = async (student) => {
    setStudentToView(student);
    setShowViewModal(true);
    await reloadScholarships(student.id);
  };

  const handleRevokeSingleSubject = async (subjectId, subjectName) => {
    if (!studentToView) return;
    if (!window.confirm(`Are you sure you want to REVOKE the scholarship for "${subjectName}"?`)) return;

    setActionLoadingId(subjectId);
    try {
      await revokeSpecificScholarship(studentToView.id, subjectId);
      alert(`Scholarship for "${subjectName}" revoked successfully!`);
      await reloadScholarships(studentToView.id);
    } catch (err) {
      alert('Failed to revoke subject scholarship: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRevokeSingleTerm = async (subjectId, termNumber, targetName) => {
    if (!studentToView) return;
    if (!window.confirm(`Are you sure you want to REVOKE Term ${termNumber} access for "${targetName}"?`)) return;

    setActionLoadingId(`${subjectId}_term_${termNumber}`);
    try {
      await revokeTermFromScholarship(studentToView.id, subjectId, termNumber);
      alert(`Term ${termNumber} access revoked for "${targetName}"!`);
      await reloadScholarships(studentToView.id);
    } catch (err) {
      alert('Failed to revoke term access: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRevokeAllScholarships = async () => {
    if (!studentToView) return;
    if (!window.confirm('Are you sure you want to REVOKE ALL scholarships (Class package and all subjects) from this student?')) return;

    setGrantingPremium(true);
    try {
      await revokePremiumAccess(studentToView.id);
      alert('All scholarships revoked successfully!');
      await reloadScholarships(studentToView.id);
    } catch (err) {
      alert('Failed to revoke all scholarships: ' + (err.message || 'Unknown error'));
    } finally {
      setGrantingPremium(false);
    }
  };

  // Table columns
  const columns = [
    {
      header: 'Name',
      accessor: 'fullName',
      render: (value, row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 min-w-[2.5rem] rounded-full bg-gradient-to-br from-blue-600 to-emerald-500 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
            {value?.charAt(0) || 'S'}
          </div>
          <span className="font-medium text-gray-900">{value || 'N/A'}</span>
        </div>
      ),
    },
    {
      header: 'Email',
      accessor: 'email',
      render: (value) => <span className="text-gray-600">{value}</span>,
    },
    {
      header: 'Class',
      accessor: 'classLevel',
      render: (value) => (
        <span className="text-gray-700 font-medium">{value ? (CLASS_NAMES[value.toLowerCase()] || CLASS_NAMES[value] || value) : 'N/A'}</span>
      ),
    },
    {
      header: 'Referral',
      accessor: 'referral',
      render: (value) => <span className="text-gray-600">{formatReferral(value)}</span>,
    },
    {
      header: 'Subscription',
      accessor: 'subscriptions',
      render: (value) => {
        const hasSubscription = value && value.length > 0;
        return (
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
            hasSubscription ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {hasSubscription ? `${value.length} Active` : 'None'}
          </span>
        );
      },
    },
    {
      header: 'Status',
      accessor: 'isActive',
      render: (value) => (
        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      header: 'Actions',
      accessor: 'id',
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleViewStudent(row)}
            className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            title="View details"
          >
            <MdRemoveRedEye size={18} />
          </button>
          <button
            onClick={() => {
              setEditingStudent(row);
              setShowEditModal(true);
            }}
            className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
            title="Edit student"
          >
            <MdEdit size={18} />
          </button>
          <button
            onClick={() => handleToggleStatus(row)}
            className={`p-2 rounded-lg transition-colors ${
              row.isActive 
                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
            title={row.isActive ? 'Deactivate' : 'Activate'}
          >
            {row.isActive ? <MdToggleOff size={18} /> : <MdToggleOn size={18} />}
          </button>
          <button
            onClick={() => {
              setStudentToDelete(row);
              setShowDeleteModal(true);
            }}
            className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
            title="Delete student"
          >
            <MdDelete size={18} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full">
      {/* Page Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold text-darkNavy mb-1 md:mb-2 font-poppins">Student Management</h1>
            <p className="text-sm md:text-base text-gray-500">View and manage all students</p>
          </div>
          <button onClick={() => setShowAddModal(true)} className="flex items-center justify-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-primary text-white rounded-lg md:rounded-xl font-semibold hover:bg-blue-700 transition-all text-sm md:text-base whitespace-nowrap flex-shrink-0">
            <MdAdd size={20} />
            <span className="hidden sm:inline">Add Student</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-4 md:mb-6">
        {/* Class Filter */}
        <select
          value={classFilter}
          onChange={(e) => setClassFilter(e.target.value)}
          className="px-3 md:px-4 py-2 md:py-2.5 border border-gray-300 rounded-lg md:rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm md:text-base"
        >
          <option value="all">All Classes</option>
          <option value="jss1">Junior Secondary 1</option>
          <option value="jss2">Junior Secondary 2</option>
          <option value="jss3">Junior Secondary 3</option>
          <option value="ss1">Senior Secondary 1</option>
          <option value="ss2">Senior Secondary 2</option>
          <option value="ss3">Senior Secondary 3</option>
          <option value="utme">UTME</option>
        </select>

        {/* Subscription Filter */}
        <select
          value={subscriptionFilter}
          onChange={(e) => setSubscriptionFilter(e.target.value)}
          className="px-3 md:px-4 py-2 md:py-2.5 border border-gray-300 rounded-lg md:rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm md:text-base"
        >
          <option value="all">All Students</option>
          <option value="subscribed">Subscribed</option>
          <option value="unsubscribed">Not Subscribed</option>
        </select>

        {/* Search Bar */}
        <div className="relative flex-1">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 md:py-2.5 border border-gray-300 rounded-lg md:rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm md:text-base"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2 md:gap-3 mb-4 md:mb-6">
        <div className="bg-white p-2 md:p-3 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-xs text-gray-500 mb-0.5">Total</p>
          <p className="text-base md:text-xl font-bold text-darkNavy">{students.length}</p>
        </div>
        <div className="bg-white p-2 md:p-3 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-xs text-gray-500 mb-0.5">JSS1</p>
          <p className="text-base md:text-xl font-bold text-darkNavy">
            {students.filter(s => (s.classLevel || '').toLowerCase() === 'jss1').length}
          </p>
        </div>
        <div className="bg-white p-2 md:p-3 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-xs text-gray-500 mb-0.5">JSS2</p>
          <p className="text-base md:text-xl font-bold text-darkNavy">
            {students.filter(s => (s.classLevel || '').toLowerCase() === 'jss2').length}
          </p>
        </div>
        <div className="bg-white p-2 md:p-3 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-xs text-gray-500 mb-0.5">JSS3</p>
          <p className="text-base md:text-xl font-bold text-darkNavy">
            {students.filter(s => (s.classLevel || '').toLowerCase() === 'jss3').length}
          </p>
        </div>
        <div className="bg-white p-2 md:p-3 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-xs text-gray-500 mb-0.5">SS1</p>
          <p className="text-base md:text-xl font-bold text-darkNavy">
            {students.filter(s => (s.classLevel || '').toLowerCase() === 'ss1').length}
          </p>
        </div>
        <div className="bg-white p-2 md:p-3 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-xs text-gray-500 mb-0.5">SS2</p>
          <p className="text-base md:text-xl font-bold text-darkNavy">
            {students.filter(s => (s.classLevel || '').toLowerCase() === 'ss2').length}
          </p>
        </div>
        <div className="bg-white p-2 md:p-3 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-xs text-gray-500 mb-0.5">SS3</p>
          <p className="text-base md:text-xl font-bold text-darkNavy">
            {students.filter(s => (s.classLevel || '').toLowerCase() === 'ss3').length}
          </p>
        </div>
        <div className="bg-white p-2 md:p-3 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-xs text-gray-500 mb-0.5">UTME</p>
          <p className="text-base md:text-xl font-bold text-darkNavy">
            {students.filter(s => (s.classLevel || '').toLowerCase() === 'utme').length}
          </p>
        </div>
        <div className="bg-white p-2 md:p-3 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-xs text-gray-500 mb-0.5">Subscribed</p>
          <p className="text-base md:text-xl font-bold text-green-600">
            {students.filter(s => s.subscriptions && s.subscriptions.length > 0).length}
          </p>
        </div>
      </div>

      {/* Loading/Empty States */}
      {loading ? (
        <div className="py-12 md:py-16 text-center text-gray-400 text-sm">Loading students...</div>
      ) : filteredStudents.length === 0 ? (
        <div className="py-12 md:py-16 text-center text-gray-400 text-sm">No students found</div>
      ) : (
        <>
          {/* Mobile Card List - Hidden on Desktop */}
          <div className="block lg:hidden space-y-3">
            {filteredStudents.map((student) => (
              <div
                key={student.id}
                className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm"
              >
                {/* Student Header with Avatar and View Button */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="hidden sm:flex w-12 h-12 min-w-[3rem] rounded-full bg-gradient-to-br from-blue-600 to-emerald-500 items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                      {student.fullName?.charAt(0) || 'S'}
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <h3 className="font-semibold text-gray-900 text-base truncate text-left">
                        {student.fullName || 'N/A'}
                      </h3>
                      <p className="text-sm text-gray-600 truncate text-left">{student.email}</p>
                      {formatReferral(student.referral) !== 'None' && (
                        <p className="text-xs text-gray-500 truncate text-left mt-0.5">
                          Ref: {formatReferral(student.referral)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleViewStudent(student)}
                      className="p-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      title="View details"
                    >
                      <MdRemoveRedEye size={20} />
                    </button>
                    <button
                      onClick={() => {
                        const isSenior = isSeniorClass(student.classLevel);
                        setEditingStudent({
                          ...student,
                          stream: isSenior ? (student.stream || student.classType || 'Science') : '',
                          classType: isSenior ? (student.classType || student.stream || 'Science') : 'General',
                        });
                        setShowEditModal(true);
                      }}
                      className="p-2.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                      title="Edit student"
                    >
                      <MdEdit size={20} />
                    </button>
                    <button
                      onClick={() => handleToggleStatus(student)}
                      className={`p-2.5 rounded-lg transition-colors ${
                        student.isActive 
                          ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                      title={student.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {student.isActive ? <MdToggleOff size={20} /> : <MdToggleOn size={20} />}
                    </button>
                    <button
                      onClick={() => {
                        setStudentToDelete(student);
                        setShowDeleteModal(true);
                      }}
                      className="p-2.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                      title="Delete student"
                    >
                      <MdDelete size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table - Hidden on Mobile */}
          <div className="hidden lg:block bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="overflow-x-auto">
              <DataTable columns={columns} data={filteredStudents} />
            </div>
          </div>
        </>
      )}

      {/* View Student Details Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setStudentToView(null);
        }}
        title="Student Details"
        size="medium"
      >
        {studentToView && (
          <div className="space-y-4 md:space-y-6">
            {/* Student Avatar and Name */}
            <div className="flex items-center gap-3 md:gap-4 pb-4 md:pb-6 border-b border-gray-200">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-blue-600 to-emerald-500 flex items-center justify-center text-white font-bold text-xl md:text-2xl flex-shrink-0">
                {studentToView.fullName?.charAt(0) || 'S'}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg md:text-xl font-bold text-darkNavy truncate">{studentToView.fullName}</h3>
                <p className="text-sm md:text-base text-gray-500 truncate">{studentToView.email}</p>
              </div>
            </div>

            {/* Student Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-xs md:text-sm font-semibold text-gray-500 mb-1">Class Level</label>
                <p className="text-sm md:text-base text-gray-900 font-medium">
                  {studentToView.classLevel ? CLASS_NAMES[studentToView.classLevel] : 'N/A'}
                </p>
                {studentToView.stream && isSeniorClass(studentToView.classLevel) && (
                  <p className="text-sm text-gray-600 mt-1">Stream: {studentToView.stream}</p>
                )}
                {studentToView.classLevel === 'UTME' && studentToView.utmeSubjects && studentToView.utmeSubjects.length > 0 && (
                  <p className="text-sm text-gray-600 mt-1">UTME Subjects: {studentToView.utmeSubjects.join(', ')}</p>
                )}
              </div>

              <div>
                <label className="block text-xs md:text-sm font-semibold text-gray-500 mb-1">Status</label>
                <span className={`inline-block px-3 py-1 rounded-full text-xs md:text-sm font-semibold ${
                  studentToView.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {studentToView.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-semibold text-gray-500 mb-1">Subscriptions / Scholarships</label>
                <p className="text-sm md:text-base text-gray-900 font-medium">
                  {checkingPremium
                    ? 'Loading...'
                    : (scholarshipDetails.totalActive > 0 
                        ? `${scholarshipDetails.totalActive} Active` 
                        : `${studentToView.subscriptions?.length || 0} Active`)}
                </p>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-semibold text-gray-500 mb-1">Joined Date</label>
                <p className="text-sm md:text-base text-gray-900 font-medium">
                  {studentToView.createdAt ? (
                    studentToView.createdAt.toDate ? 
                      studentToView.createdAt.toDate().toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      }) : new Date(studentToView.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                  ) : 'N/A'}
                </p>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-semibold text-gray-500 mb-1">Referral</label>
                <p className="text-sm md:text-base text-gray-900 font-medium">
                  {formatReferral(studentToView.referral)}
                </p>
              </div>
            </div>

            {/* Granted Scholarships & Access (Granular Management) */}
            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm md:text-base font-bold text-darkNavy">
                    Active Scholarships & Access
                  </h4>
                  {scholarshipDetails.totalActive > 0 && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                      {scholarshipDetails.totalActive}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setShowScholarshipModal(true)}
                  className="px-3 py-1 rounded-lg text-xs font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 transition-colors"
                >
                  + Grant Scholarship
                </button>
              </div>

              {checkingPremium ? (
                <div className="py-4 text-center text-xs text-gray-500">Checking active access...</div>
              ) : scholarshipDetails.totalActive === 0 ? (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-center">
                  <p className="text-xs md:text-sm text-gray-500">No active scholarships granted to this student.</p>
                </div>
              ) : (
                <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1">
                  {/* Full Class Package */}
                  {scholarshipDetails.classPackage && (
                    <div className="p-3 bg-blue-50/70 border border-blue-200 rounded-xl">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-xs md:text-sm text-blue-950">Full Class Package</span>
                            <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-blue-100 text-blue-700 uppercase">
                              {scholarshipDetails.classPackage.accessMode === 'term' ? 'Term-Based' : 'Monthly (All Terms)'}
                            </span>
                          </div>
                          {scholarshipDetails.classPackage.expiresAt && (
                            <p className="text-[11px] text-gray-500 mt-1">
                              Expires: {new Date(scholarshipDetails.classPackage.expiresAt).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          disabled={actionLoadingId === '_class_package' || grantingPremium}
                          onClick={() => handleRevokeSingleSubject('_class_package', 'Full Class Package')}
                          className="px-2.5 py-1 text-xs font-semibold bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
                        >
                          {actionLoadingId === '_class_package' ? 'Revoking...' : 'Revoke Package'}
                        </button>
                      </div>

                      {/* Unlocked Terms for Class Package */}
                      {scholarshipDetails.classPackage.unlockedTerms?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-blue-100 flex items-center gap-2 flex-wrap">
                          <span className="text-[11px] font-semibold text-gray-600">Active Terms:</span>
                          {scholarshipDetails.classPackage.unlockedTerms.map((t) => (
                            <span
                              key={t}
                              className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-white border border-blue-200 rounded-md text-[11px] font-semibold text-blue-800"
                            >
                              Term {t}
                              <button
                                type="button"
                                title={`Revoke Term ${t} from Class Package`}
                                disabled={actionLoadingId === `_class_package_term_${t}`}
                                onClick={() => handleRevokeSingleTerm('_class_package', t, 'Full Class Package')}
                                className="text-red-500 hover:text-red-700 font-bold ml-0.5 text-xs flex items-center"
                              >
                                {actionLoadingId === `_class_package_term_${t}` ? '...' : <MdClose size={12} />}
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Individual Subjects */}
                  {scholarshipDetails.subjects.map((subj) => {
                    const subjTitle = formatSubjectTitle(subj.id, studentToView);
                    return (
                      <div
                        key={subj.id}
                        className="p-3 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-xs md:text-sm text-gray-900">{subjTitle}</span>
                              <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-gray-100 text-gray-700 uppercase">
                                {subj.tier === 'bundle' ? 'Bundle' : 'Single'}
                              </span>
                            </div>
                            {subj.expiresAt && (
                              <p className="text-[11px] text-gray-500 mt-1">
                                Expires: {new Date(subj.expiresAt).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            disabled={actionLoadingId === subj.id || grantingPremium}
                            onClick={() => handleRevokeSingleSubject(subj.id, subjTitle)}
                            className="px-2.5 py-1 text-xs font-semibold bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
                          >
                            {actionLoadingId === subj.id ? 'Revoking...' : 'Revoke Subject'}
                          </button>
                        </div>

                        {/* Unlocked Terms for this Subject */}
                        {subj.unlockedTerms?.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-2 flex-wrap">
                            <span className="text-[11px] font-semibold text-gray-600">Active Terms:</span>
                            {subj.unlockedTerms.map((t) => (
                              <span
                                key={t}
                                className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-gray-50 border border-gray-200 rounded-md text-[11px] font-semibold text-gray-800"
                              >
                                Term {t}
                                <button
                                  type="button"
                                  title={`Revoke Term ${t} from ${subjTitle}`}
                                  disabled={actionLoadingId === `${subj.id}_term_${t}`}
                                  onClick={() => handleRevokeSingleTerm(subj.id, t, subjTitle)}
                                  className="text-red-500 hover:text-red-700 font-bold ml-0.5 text-xs flex items-center"
                                >
                                  {actionLoadingId === `${subj.id}_term_${t}` ? '...' : <MdClose size={12} />}
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 pt-4 border-t border-gray-200">
              <div>
                {scholarshipDetails.totalActive > 1 && (
                  <button
                    type="button"
                    disabled={grantingPremium || checkingPremium}
                    onClick={handleRevokeAllScholarships}
                    className="w-full sm:w-auto px-4 py-2 border border-red-300 text-red-600 hover:bg-red-50 rounded-lg font-semibold text-xs md:text-sm transition-colors"
                  >
                    {grantingPremium ? 'Revoking All...' : 'Revoke All Scholarships'}
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowViewModal(false);
                    setStudentToView(null);
                  }}
                  className="w-full sm:w-auto px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg md:rounded-xl font-semibold hover:bg-gray-200 transition-colors text-sm md:text-base"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        </Modal>
        {/* Add Student Modal */}
        <Modal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          title="Add New Student"
          size="medium"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={newStudent.fullName}
                onChange={(e) => setNewStudent({ ...newStudent, fullName: e.target.value })}
                placeholder="e.g., Jane Doe"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email <span className="text-red-500">*</span></label>
              <input
                type="email"
                value={newStudent.email}
                onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                placeholder="e.g., student@example.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Password <span className="text-red-500">*</span></label>
              <input
                type="password"
                value={newStudent.password}
                onChange={(e) => setNewStudent({ ...newStudent, password: e.target.value })}
                placeholder="Password"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
            </div>
            <div className={isSeniorClass(newStudent.classLevel) ? "grid grid-cols-2 gap-4" : ""}>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Class Level <span className="text-red-500">*</span></label>
                <select
                  value={newStudent.classLevel}
                  onChange={(e) => {
                    const val = e.target.value;
                    const senior = isSeniorClass(val);
                    setNewStudent({ 
                      ...newStudent, 
                      classLevel: val,
                      stream: senior ? (newStudent.stream && newStudent.stream !== 'General' ? newStudent.stream : 'Science') : '',
                    });
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                >
                  <option value="">Select class</option>
                  <option value="jss1">Junior Secondary 1</option>
                  <option value="jss2">Junior Secondary 2</option>
                  <option value="jss3">Junior Secondary 3</option>
                  <option value="ss1">Senior Secondary 1</option>
                  <option value="ss2">Senior Secondary 2</option>
                  <option value="ss3">Senior Secondary 3</option>
                  <option value="utme">UTME</option>
                </select>
              </div>
              {isSeniorClass(newStudent.classLevel) && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Stream</label>
                  <select
                    value={newStudent.stream || 'Science'}
                    onChange={(e) => setNewStudent({ ...newStudent, stream: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  >
                    <option value="Science">Science</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Art">Art</option>
                  </select>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
              <input
                type="tel"
                value={newStudent.phoneNumber}
                onChange={(e) => setNewStudent({ ...newStudent, phoneNumber: e.target.value })}
                placeholder="e.g., 08012345678"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="activeStudent"
                checked={newStudent.isActive}
                onChange={(e) => setNewStudent({ ...newStudent, isActive: e.target.checked })}
                className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary/20"
              />
              <label htmlFor="activeStudent" className="text-sm font-medium text-gray-700">Active (visible to users)</label>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
              >Cancel</button>
              <button
                onClick={async () => {
                  if (!newStudent.fullName || !newStudent.email || !newStudent.password || !newStudent.classLevel) {
                    alert('Please fill all required fields');
                    return;
                  }
                  const isSenior = isSeniorClass(newStudent.classLevel);
                  try {
                    await createUser({ 
                      ...newStudent, 
                      role: USER_ROLES.STUDENT, 
                      assignedSubjects: [],
                      stream: isSenior ? (newStudent.stream || 'Science') : null,
                      classType: isSenior ? (newStudent.stream || 'Science') : 'General',
                      phoneNumber: newStudent.phoneNumber || null
                    });
                    setShowAddModal(false);
                    setNewStudent({ fullName: '', email: '', password: '', classLevel: '', stream: '', phoneNumber: '', isActive: true });
                    alert('Student added successfully');
                  } catch (error) {
                    console.error('Error adding student:', error);
                    alert('Failed to add student');
                  }
                }}
                className="px-6 py-2.5 bg-primary text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
              >Add Student</button>
            </div>
          </div>
        </Modal>

        {/* Edit Student Modal */}
        <Modal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingStudent(null);
          }}
          title="Edit Student"
          size="medium"
        >
          {editingStudent && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={editingStudent.fullName || ''}
                  onChange={(e) => setEditingStudent({ ...editingStudent, fullName: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={editingStudent.email || ''}
                  disabled
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">Email cannot be changed after creation.</p>
              </div>
              <div className={isSeniorClass(editingStudent.classLevel) ? "grid grid-cols-2 gap-4" : ""}>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Class Level</label>
                  <select
                    value={(editingStudent.classLevel || '').toLowerCase()}
                    onChange={(e) => {
                      const val = e.target.value;
                      const senior = isSeniorClass(val);
                      setEditingStudent({ 
                        ...editingStudent, 
                        classLevel: val,
                        stream: senior ? (editingStudent.stream && editingStudent.stream !== 'General' ? editingStudent.stream : 'Science') : '',
                        classType: senior ? (editingStudent.classType && editingStudent.classType !== 'General' ? editingStudent.classType : 'Science') : 'General',
                      });
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  >
                    <option value="">Select class</option>
                    <option value="jss1">Junior Secondary 1</option>
                    <option value="jss2">Junior Secondary 2</option>
                    <option value="jss3">Junior Secondary 3</option>
                    <option value="ss1">Senior Secondary 1</option>
                    <option value="ss2">Senior Secondary 2</option>
                    <option value="ss3">Senior Secondary 3</option>
                    <option value="utme">UTME</option>
                  </select>
                </div>
                {isSeniorClass(editingStudent.classLevel) && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Stream</label>
                    <select
                      value={editingStudent.stream || editingStudent.classType || 'Science'}
                      onChange={(e) => setEditingStudent({ 
                        ...editingStudent, 
                        stream: e.target.value,
                        classType: e.target.value
                      })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    >
                      <option value="Science">Science</option>
                      <option value="Commercial">Commercial</option>
                      <option value="Art">Art</option>
                    </select>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={editingStudent.phoneNumber || ''}
                  onChange={(e) => setEditingStudent({ ...editingStudent, phoneNumber: e.target.value })}
                  placeholder="e.g., 08012345678"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="editActiveStudent"
                  checked={editingStudent.isActive !== false}
                  onChange={(e) => setEditingStudent({ ...editingStudent, isActive: e.target.checked })}
                  className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary/20"
                />
                <label htmlFor="editActiveStudent" className="text-sm font-medium text-gray-700">Active (visible to users)</label>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingStudent(null);
                  }}
                  className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                >Cancel</button>
                <button
                  onClick={async () => {
                    if (!editingStudent.fullName || !editingStudent.classLevel) {
                      alert('Full name and Class Level are required.');
                      return;
                    }
                    const isSenior = isSeniorClass(editingStudent.classLevel);
                    try {
                      await updateUser(editingStudent.id, {
                        fullName: editingStudent.fullName,
                        phoneNumber: editingStudent.phoneNumber || null,
                        classLevel: editingStudent.classLevel,
                        stream: isSenior ? (editingStudent.stream || 'Science') : null,
                        classType: isSenior ? (editingStudent.stream || editingStudent.classType || 'Science') : 'General',
                        isActive: editingStudent.isActive,
                      });
                      setShowEditModal(false);
                      setEditingStudent(null);
                      alert('Student updated successfully');
                    } catch (error) {
                      console.error('Error updating student:', error);
                      alert('Failed to update student');
                    }
                  }}
                  className="px-6 py-2.5 bg-primary text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                >Save Changes</button>
              </div>
            </div>
          )}
        </Modal>

        {/* Grant Scholarship Modal */}
        <Modal
          isOpen={showScholarshipModal}
          onClose={() => {
            setShowScholarshipModal(false);
            setScholarshipSelectedSubjects([]);
          }}
          title="Grant Scholarship"
          size="medium"
        >
          <div className="space-y-5">
            <p className="text-sm text-gray-600">
              Configure the scholarship package for <strong>{studentToView?.fullName}</strong> ({studentToView?.classLevel ? CLASS_NAMES[studentToView?.classLevel] : 'N/A'}).
            </p>
            
            {/* Access Mode */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Access Type</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setScholarshipAccessMode('monthly')}
                  className={`px-4 py-3 rounded-xl border text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                    scholarshipAccessMode === 'monthly'
                      ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/20'
                      : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Monthly Access (All Terms)
                </button>
                <button
                  type="button"
                  onClick={() => setScholarshipAccessMode('term')}
                  className={`px-4 py-3 rounded-xl border text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                    scholarshipAccessMode === 'term'
                      ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/20'
                      : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Term-Based Access
                </button>
              </div>
            </div>

            {/* Term Selection (If Term-Based) */}
            {scholarshipAccessMode === 'term' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Select Term</label>
                <div className="grid grid-cols-3 gap-3">
                  {[1, 2, 3].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setScholarshipTerm(t)}
                      className={`px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all ${
                        scholarshipTerm === t
                          ? 'border-blue-600 bg-blue-600 text-white'
                          : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Term {t}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Package Tier */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Package Tier</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { id: 'class', label: 'Full Class Access', desc: 'All subjects' },
                  { id: 'single', label: 'Single Subject', desc: '1 subject' },
                  { id: 'bundle_3', label: '3-Subject Bundle', desc: '3 subjects' },
                  { id: 'bundle_5', label: '5-Subject Bundle', desc: '5 subjects' },
                ].map((tier) => (
                  <button
                    key={tier.id}
                    type="button"
                    onClick={() => {
                      setScholarshipTier(tier.id);
                      setScholarshipSelectedSubjects([]);
                    }}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      scholarshipTier === tier.id
                        ? 'border-emerald-500 bg-emerald-50/50 text-emerald-900 ring-2 ring-emerald-500/20'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <p className="font-bold text-xs md:text-sm">{tier.label}</p>
                    <p className="text-[11px] text-gray-500">{tier.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Subject Picker (If not full class access) */}
            {scholarshipTier !== 'class' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-semibold text-gray-700">
                    Select Subject(s)
                  </label>
                  <span className="text-xs font-bold text-primary">
                    Selected: {scholarshipSelectedSubjects.length} / {
                      scholarshipTier === 'single' ? 1 : (scholarshipTier === 'bundle_3' ? 3 : 5)
                    }
                  </span>
                </div>
                <div className="max-h-48 overflow-y-auto p-3 border border-gray-200 rounded-xl space-y-1.5 bg-gray-50/50">
                  {getAvailableSubjectsForStudent(studentToView).map((subj) => {
                    const isChecked = scholarshipSelectedSubjects.includes(subj.id);
                    return (
                      <div
                        key={subj.id}
                        onClick={() => handleSubjectToggle(subj.id)}
                        className={`flex items-center justify-between p-2 rounded-lg cursor-pointer text-xs md:text-sm font-medium transition-all ${
                          isChecked
                            ? 'bg-blue-50 text-blue-800 font-semibold'
                            : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <span>{subj.name}</span>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {}}
                          className="w-4 h-4 text-primary rounded"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Duration */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Duration</label>
              <select
                value={scholarshipDuration}
                onChange={(e) => setScholarshipDuration(Number(e.target.value))}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm"
              >
                <option value={1}>1 Month</option>
                <option value={3}>3 Months</option>
                <option value={6}>6 Months</option>
                <option value={12}>1 Year (12 Months)</option>
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                onClick={() => {
                  setShowScholarshipModal(false);
                  setScholarshipSelectedSubjects([]);
                }}
                className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                disabled={grantingPremium}
                onClick={async () => {
                  const maxAllowed = scholarshipTier === 'single' ? 1 : (scholarshipTier === 'bundle_3' ? 3 : (scholarshipTier === 'bundle_5' ? 5 : 0));
                  
                  if (scholarshipTier !== 'class') {
                    if (scholarshipSelectedSubjects.length === 0) {
                      alert('Please select at least one subject for the scholarship.');
                      return;
                    }
                    if (scholarshipSelectedSubjects.length < maxAllowed) {
                      alert(`Please select ${maxAllowed} subjects for this ${scholarshipTier === 'single' ? 'single subject' : 'bundle'}.`);
                      return;
                    }
                  }

                  setGrantingPremium(true);
                  try {
                    await grantPremiumAccess(studentToView.id, {
                      months: scholarshipDuration,
                      tier: scholarshipTier === 'class' ? 'class' : (scholarshipTier === 'single' ? 'single' : 'bundle'),
                      accessMode: scholarshipAccessMode,
                      selectedTerm: Number(scholarshipTerm),
                      subjectIds: scholarshipTier === 'class' ? [] : scholarshipSelectedSubjects,
                    });
                    alert('Scholarship granted successfully!');
                    setIsPremium(true);
                    setShowScholarshipModal(false);
                    setScholarshipSelectedSubjects([]);
                    await reloadScholarships(studentToView.id);
                  } catch (error) {
                    console.error('Error granting scholarship:', error);
                    alert('Failed to grant scholarship: ' + (error.message || 'Unknown error'));
                  } finally {
                    setGrantingPremium(false);
                  }
                }}
                className="px-6 py-2.5 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-colors text-sm disabled:opacity-50"
              >
                {grantingPremium ? 'Granting...' : 'Grant Scholarship'}
              </button>
            </div>
          </div>
        </Modal>
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setStudentToDelete(null);
        }}
        title="Delete Student"
        size="small"
      >
        <div>
          <p className="mb-3 text-gray-700">
            Are you sure you want to delete{' '}
            <strong className="text-darkNavy">{studentToDelete?.fullName}</strong>?
          </p>
          <p className="text-sm text-red-600 mb-6">This action cannot be undone.</p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setStudentToDelete(null);
              }}
              className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button 
              onClick={handleDeleteStudent}
              disabled={isDeleting}
              className={`px-6 py-2.5 rounded-xl font-semibold transition-all hover:shadow-lg ${
                isDeleting ? 'bg-red-400 cursor-not-allowed text-white' : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              {isDeleting ? 'Deleting...' : 'Delete Student'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Students;
